import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Divider } from 'antd';
import { FormattedMessage } from 'react-intl';
import Pficon from 'components/Pficon';
import classNames from 'classnames';
import { isNaN } from 'lodash';

import {
  getNumericFromText,
  getNumericValueFromTextValue,
} from 'containers/QuestionnairePage/QuestionGroup/utils';
import messages from 'containers/TemplatePreviewPage/TemplateFlows/ComplexFlow/messages';
import {
  getDefaultVariableValue,
  hasFieldError,
} from 'containers/TemplatePreviewPage/utils';

import { FIELD_TYPE } from 'containers/TemplatePreviewPage/constants';
import QuestionHeader from '../QuestionHeader';
import SimpleText from '../SimpleText';
import UploadMedia from '../TextWithMedia/UploadMedia';

const NumericAndTextRepeater = ({
  question,
  onUpdateVariable,
  questionIndex,
  skipTopMargin,
  addButtonText,
}) => {
  const {
    value: values = [],
    hasError,
    isPristine,
    placeholder,
    min: minAnswers = 0,
    max: maxAnswers = 100,
  } = question;

  const underMinCount = Math.max(minAnswers - values.length, 0);
  const overMaxCount = Math.max(values.length - maxAnswers, 0);

  const missingMessage =
    underMinCount === 1
      ? messages.validation.fieldIncomplete
      : messages.validation.fieldIncompletePlural;

  const extraValuesMessage =
    overMaxCount === 1
      ? messages.validation.fieldHasExtra
      : messages.validation.fieldHasExtraPlural;

  const onUpdate = useCallback(
    (value, updateIndex) => {
      const updatedValues = values.map((existingValue, index) => {
        if (index === updateIndex) {
          return {
            ...existingValue,
            ...value,
          };
        }
        return existingValue;
      });
      onUpdateVariable(updatedValues);
    },
    [onUpdateVariable, values],
  );

  const onUpdateTextValue = useCallback(
    (index) => (value) => {
      onUpdate(
        { text: value, numericValue: getNumericValueFromTextValue(value) },
        index,
      );
    },
    [onUpdate],
  );

  const onUpdateNumericValue = useCallback(
    (index) => (value) => {
      const numeric = getNumericFromText(value);
      const numericValue = isNaN(numeric) ? undefined : numeric;

      onUpdate({ numericValue }, index);
    },
    [onUpdate],
  );

  const onChangeMedia = useCallback(
    (index) => (media) => {
      onUpdate({ media }, index);
    },
    [onUpdate],
  );

  const onAddValue = useCallback(() => {
    onUpdateVariable([
      ...values,
      getDefaultVariableValue({ fieldType: FIELD_TYPE.TEXT_WITH_NUMERIC }),
    ]);
  }, [onUpdateVariable, values]);

  const onRemoveValue = useCallback(
    (indexToRemove) => {
      onUpdateVariable(values.filter((_, index) => index !== indexToRemove));
    },
    [onUpdateVariable, values],
  );
  const addDisabled = values.length >= maxAnswers;

  return (
    <React.Fragment>
      <QuestionHeader
        title={question.body}
        note={question.note}
        skipTopMargin={questionIndex === 0 && skipTopMargin}
      />
      <ul className="flex flex-col margin-none padding-none">
        {values.map((value = {}, index) => {
          const textQuestion = {
            body: null,
            fieldType: FIELD_TYPE.TEXT,
            value: value.text,
            placeholder,
            required: true,
            isPristine,
          };
          textQuestion.hasError = hasFieldError(textQuestion);
          const numericQuestion = {
            body: null,
            fieldType: FIELD_TYPE.NUMERIC,
            value: value.numericValue,
            placeholder: '',
            required: true,
            isPristine,
          };
          numericQuestion.hasError = hasFieldError(numericQuestion);

          const deleteDisabled = values.length <= minAnswers;

          return (
            // eslint-disable-next-line react/no-array-index-key
            <li className="flex flex-items-center margin-btm-sm" key={index}>
              <div className="flex flex-items-center flex-1 position-relative">
                <SimpleText
                  question={textQuestion}
                  onUpdateVariable={onUpdateTextValue(index)}
                  hideHeader
                  hideErrors
                />
                <span className="margin-horizontal-sm text-wrap-none">
                  Just the number:
                </span>
                <SimpleText
                  question={numericQuestion}
                  onUpdateVariable={onUpdateNumericValue(index)}
                  hideHeader
                  hideErrors
                />
              </div>

              <Divider
                type="vertical"
                className="vertical-margin-none horizontal-margin-xs background-grey-5"
                style={{ height: 42 }}
              />

              <UploadMedia
                supportedMediaTypes={['image']}
                mediaValue={value.media}
                onChangeMedia={onChangeMedia(index)}
              />
              <Divider
                type="vertical"
                className="vertical-margin-none horizontal-margin-xs background-grey-5"
                style={{ height: 42 }}
              />
              <Button
                disabled={deleteDisabled}
                type="button"
                onClick={() => onRemoveValue(index)}
                style={{ height: 38, lineHeight: '38px' }}
                className={classNames(
                  'color-grey-10',
                  'text-align-center',
                  'padding-vertical-none',
                  'padding-horizontal-xs',
                  'border-none',
                  'background-transparent',
                  'background-transparent-hover',
                  'shadow-none',
                  {
                    'color-grey-6': deleteDisabled,
                    'color-grey-6-hover': deleteDisabled,
                    'color-grey-10': !deleteDisabled,
                  },
                )}
              >
                <Pficon type="close" className="text-size-h3" />
              </Button>
            </li>
          );
        })}
        {!isPristine && hasError && Boolean(underMinCount) && (
          <li className="display-block margin-btm-sm">
            <span className="color-red-6" key={`${question.id}-validation`}>
              <FormattedMessage
                {...missingMessage}
                values={{
                  missingValuesCount: underMinCount,
                }}
              />
            </span>
          </li>
        )}
        {!isPristine && hasError && Boolean(overMaxCount) && (
          <li className="display-block margin-btm-sm">
            <span
              className="color-red-6"
              key={`${question.id}-validation-count`}
            >
              <FormattedMessage
                {...extraValuesMessage}
                values={{
                  extraValuesCount: overMaxCount,
                }}
              />
            </span>
          </li>
        )}
        {!isPristine && hasError && !(underMinCount || overMaxCount) && (
          <li className="display-block margin-vertical-sm">
            <span className="color-red-6">
              <FormattedMessage {...messages.validation.fieldHasSubError} />
            </span>
          </li>
        )}
        <li className="display-block">
          <Button
            type="button"
            onClick={onAddValue}
            disabled={addDisabled}
            style={{ height: 'auto' }}
            className={classNames(
              'outline-blue',
              'color-grey-1',
              'border-none',
              'text-size-secondary',
              'padding-horizontal-xs',
              'padding-vertical-xxs',
              'shadow-none',
              {
                'background-grey-8': addDisabled,
                'background-grey-8-hover': addDisabled,
                'background-grey-10': !addDisabled,
              },
            )}
          >
            {addButtonText || 'Add another'}
          </Button>
        </li>
      </ul>
    </React.Fragment>
  );
};

NumericAndTextRepeater.propTypes = {
  question: PropTypes.object,
  questionIndex: PropTypes.number,
  onUpdateVariable: PropTypes.func.isRequired,
};

export default NumericAndTextRepeater;
